"use client";

import { MenuIcon } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { buttonVariants } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

export type HelpSection = {
  id: string;
  title: string;
  summary: string;
};

function collectSections(): HelpSection[] {
  const main = document.getElementById("main");
  if (!main) return [];
  return Array.from(main.querySelectorAll<HTMLElement>(".help-section"))
    .filter((el) => Boolean(el.id))
    .map((el) => ({
      id: el.id,
      title: el.dataset.helpTitle ?? el.id,
      summary: el.dataset.helpSummary ?? "",
    }));
}

function scrollToSection(id: string): void {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  const url = new URL(window.location.href);
  url.hash = id;
  window.history.pushState(null, "", url.toString());
}

function TocList({
  sections,
  activeId,
  onPick,
}: {
  sections: HelpSection[];
  activeId: string;
  onPick: (id: string) => void;
}): React.ReactElement {
  return (
    <ul className="space-y-0.5 text-sm">
      {sections.map((s) => (
        <li key={s.id}>
          <button
            type="button"
            className={cn(
              "hover:bg-muted w-full rounded-md px-2 py-1.5 text-left transition-colors",
              activeId === s.id && "bg-muted font-medium text-foreground",
            )}
            onClick={() => {
              onPick(s.id);
            }}
          >
            <span className="text-foreground">{s.title}</span>
            {s.summary ? (
              <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">
                {s.summary}
              </span>
            ) : null}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function HelpToc(): React.ReactElement {
  const [sections, setSections] = useState<HelpSection[]>([]);
  const [activeId, setActiveId] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    const next = collectSections();
    setSections(next);
    const hash = window.location.hash.slice(1);
    if (hash && next.some((s) => s.id === hash)) {
      setActiveId(hash);
    }
  }, []);

  useEffect(() => {
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting && e.target.id)
          .map((e) => e.target.id);
        if (visible.length === 0) return;
        const order = new Map(sections.map((s, i) => [s.id, i]));
        visible.sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
        setActiveId(visible[0] ?? "");
      },
      {
        root: null,
        rootMargin: "-56px 0px -52% 0px",
        threshold: [0, 0.05, 0.15, 0.35],
      },
    );

    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
  }, [sections]);

  const handlePick = useCallback((id: string) => {
    scrollToSection(id);
    setActiveId(id);
    setSheetOpen(false);
  }, []);

  if (sections.length === 0) {
    return (
      <p className="text-muted-foreground text-xs">未找到带目录标记的章节。</p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="lg:hidden">
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger
            type="button"
            className={cn(
              buttonVariants({ variant: "outline", size: "sm" }),
              "w-full justify-center gap-2",
            )}
          >
            <MenuIcon className="size-4" />
            打开目录
          </SheetTrigger>
          <SheetContent side="left" className="w-[min(100%,20rem)] gap-0 p-0">
            <SheetHeader className="border-b border-border p-4 text-left">
              <SheetTitle className="text-base">本页目录</SheetTitle>
            </SheetHeader>
            <div className="max-h-[calc(100vh-8rem)] overflow-y-auto p-4">
              <TocList
                sections={sections}
                activeId={activeId}
                onPick={handlePick}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <nav
        aria-label="章节导航"
        className="hidden lg:block"
      >
        <p className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wide">
          本页目录
        </p>
        <TocList
          sections={sections}
          activeId={activeId}
          onPick={(id) => {
            scrollToSection(id);
            setActiveId(id);
          }}
        />
      </nav>
    </div>
  );
}
